/**
 * The /udise-apaar FAQ, in one place.
 *
 * The visible disclosure list and the FAQPage JSON-LD both read this array, so
 * the schema can never claim a question the page does not render.
 *
 * Eight of these entries are the questions the page already carried, moved here
 * word for word. The two additions — what an APAAR ID is here, and the five
 * steps to a UDISE+ submission — restate the feature grid and the process
 * section further up the same page. Nothing states a government rule that the
 * page does not already state itself.
 */

export interface UdiseFaq {
  question: string
  answer: string
}

export const UDISE_APAAR_FAQS: UdiseFaq[] = [
  {
    question: 'How does Euclideum integrate with UDISE+?',
    answer:
      'Euclideum uses secure API integration to connect directly with the UDISE+ portal. Once connected, it automatically maps your institutional data to UDISE+ fields, validates the information, and enables one-click submission. The entire process is encrypted and follows government security protocols.',
  },
  {
    question:
      'What is an APAAR ID, and how does Euclideum create one for each student?',
    answer:
      'APAAR is the student identity Euclideum manages alongside your UDISE+ record. The platform creates and manages APAAR IDs in bulk for every student on the roll, following government guidelines, and keeps each ID attached to the same student record the rest of the system works from, so an ID issued once is available wherever that student appears, without being typed in again.',
  },
  {
    question: 'Can we generate APAAR IDs for existing students?',
    answer:
      'Yes, absolutely! Euclideum can generate APAAR IDs in bulk for all your existing students. Simply upload your student database or sync with your existing system, and our platform will automatically generate and assign unique APAAR IDs following government guidelines.',
  },
  {
    question: 'What are the five steps to getting UDISE+ ready with Euclideum?',
    answer:
      'Connect: link Euclideum with the UDISE+ portal. Map: data fields are mapped automatically. Validate: the data is verified automatically before anything leaves your institution. Generate: APAAR IDs are created in bulk. Submit: one-click submission to UDISE+.',
  },
  {
    question: 'What happens if UDISE+ requirements change?',
    answer:
      "Our team continuously monitors UDISE+ updates and government circulars. When requirements change, we automatically update our platform to ensure continued compliance. You'll receive notifications about any changes and your system will be updated seamlessly without any disruption.",
  },
  {
    question: 'Is our data secure?',
    answer:
      'Absolutely. We are ISO 27001 certified and use bank-grade encryption for all data transmission and storage. Our servers are hosted in secure, government-approved data centers in India. We follow strict data protection protocols and never share your data with third parties.',
  },
  {
    // Scope named explicitly. This page's "2-3 weeks" and /implementation's
    // four-week programme describe different pieces of work, and stating only
    // one of them left the site appearing to contradict itself in a page
    // Google can quote directly.
    question: 'How long does UDISE+ and APAAR setup take?',
    answer:
      'UDISE+ and APAAR setup typically takes 2-3 weeks from start to finish, covering initial setup, data mapping, validation testing and training. Institutions that already run digital systems are often faster. A full campus ERP rollout is a separate piece of work and follows the four-week implementation programme.',
  },
  {
    question: 'Can we manage multiple campuses?',
    answer:
      'Yes, Euclideum supports multi-campus management. You can manage UDISE+ compliance for all your campuses from a single dashboard, with separate submissions for each UDISE+ code while maintaining centralized control and oversight.',
  },
  {
    // Aligned to the hours actually published on /support. The previous answer
    // promised "24/7 technical assistance", which /support contradicts — and
    // this answer is now emitted as FAQPage structured data, which Google can
    // surface verbatim, so an unsupportable promise here is a real liability.
    question: 'What support is provided after implementation?',
    answer:
      "Post-implementation support covers technical assistance during published support hours (Monday to Friday, 9:00 AM to 6:00 PM IST), plus regular system updates, training for new staff, and dedicated support through UDISE+ submission periods. Our team ensures you're never alone in managing compliance.",
  },
  {
    question: 'Can we generate custom reports?',
    answer:
      'Yes, besides standard UDISE+ reports, you can generate custom reports for internal use. Our platform provides detailed analytics on student data, compliance status, and submission history. All reports can be exported in multiple formats including PDF and Excel.',
  },
]
